import { merge } from 'lodash';
import { useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { UseQueryOptions } from 'react-query/types/react/types';

import { TerminalContext } from '../TerminalInit';
import { ApiQueryParams, buildApiEndpoint, queryEnabled, queryParamsToCacheKeys } from './baseQueryParams';
import { ApiError, FetchOptions, authEnabled, fetchApi } from './utils';

export enum DefaultViewSetActions {
    LIST = 'list',
    DETAIL = 'detail',
    CREATE = 'create',
    UPDATE = 'update',
    DELETE = 'delete',
}

export interface ApiViewSet {
    baseName: string;
    endpointPrefix?: string;
    endpoints?: {
        [key in string]?: string | null;
    };
}

export interface Page<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

export type DetailOptions = (
    | {
          id: string | number;
          url?: never;
      }
    | {
          id?: never;
          url: string;
      }
) & {
    customAction?: string;
};

// Has at least one of id or url
export type ResourceId =
    | {
          id: string | number;
          url?: string;
      }
    | {
          id?: string | number;
          url: string;
      };

export function isResourceId(obj: unknown): obj is ResourceId {
    const resourceId = obj as ResourceId;
    return resourceId.id !== undefined || resourceId.url !== undefined;
}

export function getEndpointPath({ viewSet }: { viewSet: ApiViewSet }): string {
    if (viewSet.endpointPrefix) {
        return `${viewSet.endpointPrefix}/${viewSet.baseName}/`;
    }
    return `/${viewSet.baseName}/`;
}

export function getEndpoint(viewSet: ApiViewSet, action: DefaultViewSetActions | string, id?: string | number | null): string {
    let endpoint = viewSet.endpoints ? viewSet.endpoints[action] : undefined;
    if (endpoint) {
        if (endpoint.includes('{id}') && id) {
            endpoint = endpoint.replace('{id}', `${id}`);
        }
        return endpoint;
    }

    if (action === DefaultViewSetActions.LIST || action === DefaultViewSetActions.CREATE) {
        return getEndpointPath({ viewSet });
    } else if (action === DefaultViewSetActions.DETAIL || action === DefaultViewSetActions.UPDATE || action === DefaultViewSetActions.DELETE) {
        return `${getEndpointPath({ viewSet })}${id}/`;
    } else {
        console.warn('Unexpected viewset action', action);
        return getEndpointPath({ viewSet });
    }
}

export function apiList<T, S extends string | number>(
    viewSet: ApiViewSet,
    queryParams?: ApiQueryParams<S> | null,
    fetchOptions?: FetchOptions
): () => Promise<T[]> {
    return async (): Promise<T[]> => {
        const baseEndpoint = getEndpoint(viewSet, DefaultViewSetActions.LIST);
        const endpoint = buildApiEndpoint(baseEndpoint, queryParams);
        const response = await fetchApi(endpoint, undefined, fetchOptions);
        if (!response.ok) {
            let json;
            try {
                json = await response.json();
            } catch (e) {
                throw new ApiError(`Error fetching list of ${viewSet.baseName}`, response.status);
            }
            throw new ApiError(`Error fetching list of ${viewSet.baseName}`, response.status, json);
        }
        return await response.json();
    };
}

interface ApiDetailProps<TQueryParams extends string | number> {
    viewSet: ApiViewSet;
    options: DetailOptions;
    queryParams?: ApiQueryParams<TQueryParams>;
    fetchOptions?: FetchOptions;
}

export function apiDetail<T, TQueryParams extends string | number>({
    viewSet,
    queryParams,
    options,
    fetchOptions,
}: ApiDetailProps<TQueryParams>): () => Promise<T> {
    return async (): Promise<T> => {
        let baseEndpoint = options.url;
        if (!baseEndpoint) {
            if (!options.id) {
                console.trace('Unexpected empty url and id', viewSet, options);
            }
            baseEndpoint = getEndpoint(viewSet, options?.customAction ? options.customAction : DefaultViewSetActions.DETAIL, options.id);
        }
        const endpoint = buildApiEndpoint(baseEndpoint, queryParams);
        console.debug('Performing detail operation', endpoint);
        let response = await fetchApi(endpoint, undefined, fetchOptions);
        if (!response.ok) {
            let json;
            try {
                json = await response.json();
            } catch (e) {
                throw new ApiError(`Error fetching detail of ${viewSet.baseName}`, response.status);
            }
            throw new ApiError(`Error fetching detail of ${viewSet.baseName}`, response.status, json);
        }
        return await response.json();
    };
}

export function apiPagination<T, S extends string | number>(
    viewSet: ApiViewSet,
    queryParams?: ApiQueryParams<S> | null,
    fetchOptions?: FetchOptions
): () => Promise<T> {
    return async (): Promise<T> => {
        const baseEndpoint = getEndpoint(viewSet, DefaultViewSetActions.LIST);
        const endpoint = buildApiEndpoint(baseEndpoint, queryParams);
        const response = await fetchApi(endpoint, undefined, fetchOptions);
        if (!response.ok) {
            let json;
            try {
                json = await response.json();
            } catch (e) {
                throw new ApiError(`Error fetching  ${viewSet.baseName}`);
            }
            throw new ApiError(`Error fetching  ${viewSet.baseName}`, response.status, json);
        }
        const result = await response.json();
        return result.results;
    };
}

interface UseListProps<TQueryParams extends string | number, TData extends ResourceId> {
    viewSet: ApiViewSet;
    queryParamsEnum: { [key in string]: string };
    fetchFn: (queryParams?: ApiQueryParams<TQueryParams> | null, fetchOptions?: FetchOptions) => () => Promise<TData[]>;
    url?: string;
    queryParams?: ApiQueryParams<TQueryParams> | null;
    queryOptions?: UseQueryOptions<TData[]>;
    fetchOptions?: FetchOptions;
}

export function useList<TQueryParams extends string | number, TData extends ResourceId>({
    viewSet,
    queryParamsEnum,
    fetchFn,
    url,
    queryParams,
    queryOptions,
    fetchOptions,
}: UseListProps<TQueryParams, TData>) {
    const queryClient = useQueryClient();

    const terminalContext = useContext(TerminalContext);
    const mergedFetchOptions: FetchOptions = {
        includeAccessToken: terminalContext.includeAccessToken,
        accessToken: terminalContext.accessToken,
        ...fetchOptions,
    };

    const resultQueryParams: ApiQueryParams<TQueryParams> = {};
    if (url) {
        const urlComponents = new URL(url);
        const urlQueryParams = new URLSearchParams(urlComponents.search);
        merge(resultQueryParams, Object.fromEntries(urlQueryParams.entries()));
    }
    if (queryParams) {
        merge(resultQueryParams, queryParams);
    }

    const config = {
        ...queryOptions,
        enabled: queryEnabled(resultQueryParams) && authEnabled(mergedFetchOptions) && queryOptions?.enabled,
        staleTime: queryOptions?.staleTime !== undefined ? queryOptions.staleTime : Infinity,
    };

    const queryResult = useQuery<TData[]>(
        [getEndpointPath({ viewSet }), queryParamsToCacheKeys(queryParamsEnum, resultQueryParams)],
        fetchFn(resultQueryParams, mergedFetchOptions),
        config
    );

    useEffect(() => {
        if (queryResult.data) {
            const resultsPage = queryResult.data;
            // Set the detail cache as well
            for (const result of resultsPage) {
                if (result.id) {
                    queryClient.setQueryData([getEndpointPath({ viewSet }), result.id, {}], result);
                }
                if (result.url) {
                    queryClient.setQueryData([getEndpointPath({ viewSet }), result.url, {}], result);
                }
            }
        }
    }, [queryResult.data]);

    return queryResult;
}

interface UseRetrieveProps<TData extends ResourceId, TQueryParams extends string | number> {
    viewSet: ApiViewSet;
    queryParamsEnum: { [key in string]: string };
    fetchFn: (detail: DetailOptions, queryParams?: ApiQueryParams<TQueryParams>, fetchOptions?: FetchOptions) => () => Promise<TData>;
    detail?: DetailOptions | null;
    queryParams?: ApiQueryParams<TQueryParams>;
    queryOptions?: UseQueryOptions<TData, ApiError>;
    fetchOptions?: FetchOptions;
}

export function useRetrieve<TData extends ResourceId, TQueryParams extends string | number>({
    viewSet,
    queryParamsEnum,
    fetchFn,
    detail,
    queryParams,
    queryOptions,
    fetchOptions,
}: UseRetrieveProps<TData, TQueryParams>) {
    const queryClient = useQueryClient();

    const terminalContext = useContext(TerminalContext);
    const mergedFetchOptions: FetchOptions = {
        ...fetchOptions,
        includeAccessToken: terminalContext.includeAccessToken,
        accessToken: fetchOptions?.accessToken ? fetchOptions.accessToken : terminalContext.accessToken,
    };

    const queryKey = detail ? (detail.id ? detail.id : detail.url ? detail.url : '') : '';

    const config: UseQueryOptions<TData, ApiError> = {
        ...queryOptions,
        enabled: !!detail && authEnabled(mergedFetchOptions) && queryOptions?.enabled,
        staleTime: queryOptions?.staleTime !== undefined ? queryOptions.staleTime : 1000 * 60 * 5,
    };

    const queryResult = useQuery<TData, ApiError>({
        queryKey: [getEndpointPath({ viewSet }), queryKey, queryParamsToCacheKeys(queryParamsEnum, queryParams)],
        queryFn: fetchFn(detail ? detail : { url: '' }, queryParams, mergedFetchOptions),
        ...config,
    });

    useEffect(() => {
        if (queryResult.data) {
            const result = queryResult.data;
            // Make sure to set both queryKey for id and url. By default, only one or the other would be set.
            if (result.id) {
                queryClient.setQueryData([getEndpointPath({ viewSet }), result.id, {}], result);
            }
            if (result.url) {
                queryClient.setQueryData([getEndpointPath({ viewSet }), result.url, {}], result);
            }
        }
    }, [queryResult.data]);

    return queryResult;
}
